import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import { SelectItem } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ColumnInfo } from '../../../core/models';
import {
  DropdownOptionsService,
  HintDialogService,
} from '../../../core/services';
import { genCommonObserverObj } from '../../../core/utils';
import {
  EnhancedFormControl,
  FormArrayEnhanced,
  FormGroupEnhanced,
  IForm,
} from '../../../models/rt-common';
import { IdentificationTypeEnum } from '../../enums/identification-type.enum';

@Component({
  selector: 'sigv-app-identification-type-table',
  templateUrl: './identification-type-table.component.html',
  styleUrls: ['./identification-type-table.component.scss'],
})
export class IdentificationTypeTableComponent implements OnInit {
  /** 傳入formArray*/
  @Input() formArrayInput!: FormArrayEnhanced<any>;
  /** 客戶類型*/
  @Input() identityType?: string;
  /** 唯讀*/
  @Input() readonly = false;
  /** formArrayChange事件*/
  @Output() formArrayChange = new EventEmitter<{
    identificationTypeFormArray: FormArrayEnhanced<any>;
    changedIndex: number | null;
    action: 'add' | 'remove' | 'update';
  }>();
  /** 必填 */
  @Input() required = false;

  //欄位名稱
  displayColumns = [
    {
      field: 'identityNo',
      header: 'Library.ConsumerBaseInfo.identityNo',
    },
    {
      field: 'identificationType',
      header: 'Library.ConsumerBaseInfo.identificationType',
    },
  ] as ColumnInfo[];

  //證件別選項
  identificationTypeOptions: SelectItem[] = [];

  /** formArray 初始化validator控制*/
  private isFormArrayInputInitialized = false;
  /** 管理取消訂閱*/
  private destroy$ = new Subject<void>();

  private readonly identificationTypeTableConfig = {
    identityNo: {
      isRequired: true,
      displayName: 'Library.ConsumerBaseInfo.identityNo',
    },
    identificationType: {
      isRequired: true,
      displayName: 'Library.ConsumerBaseInfo.identificationType',
    },
  } as any;

  constructor(
    private fb: FormBuilder,
    private hintDialogService: HintDialogService,
    public dropdownOptionsService: DropdownOptionsService,
  ) {}

  ngOnInit(): void {
    this.getAllSelectOption();

    if (!this.readonly && this.required && this.formArrayInput.length === 0) {
      this.addRow();
    }
  }

  ngDoCheck(): void {
    //待父元件傳入資料後，再做validators設定，並以isFormArrayInputInitialized控制只做一次
    if (
      !this.isFormArrayInputInitialized &&
      this.formArrayInput?.controls.length
    ) {
      this.initializeValidatorsForExistingData();
      this.isFormArrayInputInitialized = true;
    }
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**取得選項 */
  getAllSelectOption() {
    this.dropdownOptionsService
      .getCommonDropdownOptions('IdentificationType', undefined, false, true)
      .subscribe(
        genCommonObserverObj({
          next: (results) => {
            //撈到的證件別選項要以客戶類型做篩選
            this.identificationTypeOptions = results
              .filter((x) => x.dropdown.parentCode === this.identityType)
              .map((x) => ({
                label: x.dropdown.languageEnglish ?? x.dropdown.name,
                value: x.value,
              }));
          },
        }),
      );
  }

  /**父元件傳入資料設定validator */
  initializeValidatorsForExistingData() {
    this.formArrayInput.controls.forEach(
      (ctrl: AbstractControl, index: number) => {
        const group = ctrl as FormGroupEnhanced;

        // 先用 config 建立 form 的顯示與屬性
        group.genFormByConfig(this.identificationTypeTableConfig);

        this.setupValidatorForGroup(group);
      },
    );
  }
  addRow() {
    const newGroup = new FormGroupEnhanced<IForm<any>>({
      identityNo: new EnhancedFormControl(),
      identificationType: new EnhancedFormControl(),
    });
    newGroup.genFormByConfig(this.identificationTypeTableConfig);

    this.setupValidatorForGroup(newGroup);

    //#99175 新增的第一筆預帶證件類型
    if (
      this.formArrayInput.length == 0 &&
      this.identificationTypeOptions.length > 0
    ) {
      newGroup
        .get('identificationType')
        ?.setValue(this.identificationTypeOptions[0].value);
    }

    this.formArrayInput.push(newGroup);
    this.emitChange(this.formArrayInput.length - 1, 'add');
  }

  removeRow(index: number) {
    this.formArrayInput.removeAt(index);
    this.emitChange(index, 'remove');
  }

  onFieldChange(index: number) {
    this.validateDuplicateIdTypes(index);
    //傳入修改的index，父元件判斷最後修改的是哪筆
    this.emitChange(index, 'update');
  }

  emitChange(
    changedIndex: number | null = null,
    action: 'add' | 'remove' | 'update' = 'update',
  ) {
    this.formArrayChange.emit({
      identificationTypeFormArray: this.formArrayInput,
      changedIndex,
      action,
    });
  }

  // 檢查證件別是否有重複，設置錯誤
  private validateDuplicateIdTypes(index: number) {
    const identificationType = this.formArrayInput.controls.map(
      (ctrl) => ctrl.get('identificationType')?.value,
    );

    const currentValue = identificationType[index];
    if (!currentValue) return;

    // 檢查是否在其他列中已存在相同值
    const isDuplicate = identificationType.some(
      (val, i) => i !== index && val === currentValue,
    );

    const currentControl = this.formArrayInput
      .at(index)
      .get('identificationType');

    if (isDuplicate) {
      // 清除目前欄位值
      currentControl?.setValue(null);
      // 設定錯誤
      currentControl?.setErrors({ duplicate: true });
      // 顯示提示訊息
      let errMsgs = 'Library.ConsumerBaseInfo.duplicateIdentificationType';
      this.hintDialogService.showErrorHint(errMsgs);
    } else {
      // 若無重複則清除錯誤
      if (currentControl?.hasError('duplicate')) {
        currentControl.setErrors(null);
      }
    }
  }

  /**FormGroup設定validator */
  setupValidatorForGroup(group: FormGroupEnhanced) {
    const identityNoCtrl = group.get('identityNo');
    const identificationTypeCtrl = group.get('identificationType');

    // 初始化根據 identificationType 設定 validator
    const type = identificationTypeCtrl?.value;
    identityNoCtrl?.setValidators([
      Validators.required,
      this.identityNoValidator(type),
    ]);
    identityNoCtrl?.updateValueAndValidity();

    identificationTypeCtrl?.setValidators([Validators.required]);
    identificationTypeCtrl?.updateValueAndValidity();

    // 動態訂閱 identificationType 變動，調整 validator
    identificationTypeCtrl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        identityNoCtrl?.setValidators([
          Validators.required,
          this.identityNoValidator(value),
        ]);
        identityNoCtrl?.updateValueAndValidity();
      });
  }

  /**identityNo 設定欄位長度Validator */
  identityNoValidator(type: string | null): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (type === IdentificationTypeEnum.MyKad) {
        // 正規檢查 MyKad 格式與日期
        const reg = /^[0-9]{12}$/;
        if (!reg.test(value)) {
          return { idNotAllow: { message: 'MyKad must be 12 digits' } };
        }

        // 解析出生日期前 6 碼
        const dateStr = value.substr(0, 6);
        const year =
          parseInt(dateStr.substr(0, 2), 10) > 50
            ? `19${dateStr.substr(0, 2)}`
            : `20${dateStr.substr(0, 2)}`;
        const monthDay = dateStr.substr(2, 4);
        const resultDate = parse(year + monthDay, 'yyyyMMdd', new Date());

        if (!isValid(resultDate)) {
          return { idNotAllow: { message: 'Invalid birthdate in MyKad' } };
        }

        return null;
      } else {
        if (value && value.length > 20) {
          return {
            maxlength: { requiredLength: 20, actualLength: value.length },
          };
        }
      }

      return null;
    };
  }
}
